import TicketMasterApolloClient, {
  TicketMasterApolloClientProps,
} from "../../TicketMasterApolloClient/TicketMasterApolloClient.js";
import React, { forwardRef, useState } from "react";
import TicketManagers from "../TicketManagers/TicketManagers.js";
import {
  ticketEndpointContextEnum,
  systemOptionsEnum,
} from "../TicketingUtils.js";
import TicketTemplate, {
  type OnCreateTicketArgs,
  type TicketTemplateHandle,
} from "../TicketTemplate/TicketTemplate.js";
import type { TicketManager } from "../useTicketManagers/useTicketManagers.js";
import CustomPolicyPreferences from "./CustomPolicyPreferences.js";

export interface CustomPolicyProps {
  apolloClient?: TicketMasterApolloClientProps["apolloClient"];
  policyId?: string;
  wrapperClassName?: string;
  onCreateMapping?: (...args: unknown[]) => void;
  onCreateMappingError?: (...args: unknown[]) => void;
  initialValues?: unknown;
  disableInitialValues?: boolean;
  onCreatePolicy: (data: string) => void;
  onValidationChange?: (...args: unknown[]) => void;
  onDirtyChange?: (...args: unknown[]) => void;
  onTicketManagerChange?: (value?: string) => void;
  withColumnMapping?: boolean;
}

const CustomPolicy = forwardRef<TicketTemplateHandle, CustomPolicyProps>(
  (
    {
      apolloClient,
      policyId,
      wrapperClassName,
      onCreateMapping,
      onCreateMappingError,
      initialValues,
      disableInitialValues = true,
      onCreatePolicy,
      onValidationChange = () => {},
      onDirtyChange = () => {},
      onTicketManagerChange = () => {},
      withColumnMapping = false,
    },
    ref
  ) => {
    const [selectedTicketManager, setSelectedTicketManager] =
      useState<TicketManager | null>(null);
    const [mappingModalOpen, setMappingModalOpen] = useState(false);

    const onSubmit = ({ formData, mutationData }: OnCreateTicketArgs) => {
      const data = {
        value_mapping: mutationData,
        ticket_provider_id: formData?.ticketProvider,
        queue_size: formData.queueSize,
      };
      onCreatePolicy(JSON.stringify(data));
    };

    return !!apolloClient ? (
      <TicketMasterApolloClient apolloClient={apolloClient}>
        <div className={wrapperClassName}>
          {!mappingModalOpen && (
            <TicketManagers
              context={ticketEndpointContextEnum.RMQ}
              onChange={(selectedTicketManager?: TicketManager) => {
                onTicketManagerChange(selectedTicketManager?.value);
                setSelectedTicketManager(selectedTicketManager ?? null);
              }}
              label={"Ticketing System"}
              partnerExternalId={policyId}
            />
          )}
          {!!selectedTicketManager && (
            <>
              <TicketTemplate
                ref={ref}
                systemLabel={systemOptionsEnum.ARMIS}
                externalEndpointId={policyId}
                onCreateTicket={onSubmit}
                ticketManagerId={selectedTicketManager?.value}
                ticketManagerFriendlyName={selectedTicketManager?.friendlyName}
                ticketManagerMustHaveMapping={
                  selectedTicketManager?.mustHaveMapping
                }
                ticketManagerMustHaveColumnsMapping={
                  selectedTicketManager?.mustHaveColumnsMapping
                }
                ticketManagerProviders={selectedTicketManager?.providers}
                onCreateMapping={onCreateMapping}
                onCreateMappingError={onCreateMappingError}
                initialValues={initialValues}
                disableInitialValues={disableInitialValues}
                onValidationChange={onValidationChange}
                onDirtyChange={onDirtyChange}
                context={ticketEndpointContextEnum.RMQ}
                //TODO: check if it can be removed while integrating the package
                fetchPolicy={"network-only"}
                additionalPreferences={
                  <CustomPolicyPreferences policyId={policyId} />
                }
                withColumnMapping={withColumnMapping}
                onMappingModalOpen={(isOpen: boolean) =>
                  setMappingModalOpen(isOpen)
                }
              />
            </>
          )}
        </div>
      </TicketMasterApolloClient>
    ) : (
      <div className={wrapperClassName}>Error loading Manual ticket form</div>
    );
  }
);

export default CustomPolicy;
